// Small shared presentation primitives for the card rails: chips, thin status bars, cost marks and the
// family shape tile. No dropdowns anywhere (spec §8) - a chip is the only toggle in the interface.

import type { CSSProperties, ReactNode } from 'react';
import type { FamilyMeta } from '../../sim/deck';

export type ChipTone = 'neutral' | 'warn' | 'friendly' | 'hostile';

const CHIP_TONE: Record<ChipTone, { border: string; text: string }> = {
  neutral: { border: 'var(--color-slate-700)', text: 'var(--color-fog)' },
  warn: { border: 'var(--color-warn)', text: 'var(--color-warn)' },
  friendly: { border: 'var(--color-friendly)', text: 'var(--color-friendly)' },
  hostile: { border: 'var(--color-hostile)', text: 'var(--color-hostile)' },
};

export function Chip({
  children, tone = 'neutral', active = false, onClick, title, testId, disabled = false,
}: {
  children: ReactNode; tone?: ChipTone; active?: boolean; onClick?: () => void;
  title?: string; testId?: string; disabled?: boolean;
}) {
  const t = CHIP_TONE[tone];
  const style: CSSProperties = {
    borderColor: active ? t.border : 'var(--color-slate-700)',
    color: active ? 'var(--color-paper)' : t.text,
    background: active ? 'color-mix(in srgb, var(--color-slate-700) 70%, transparent)' : 'transparent',
    opacity: disabled ? 0.4 : 1,
  };
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={disabled ? undefined : onClick}
      title={title}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      className="mono rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] whitespace-nowrap"
      style={style}
    >
      {children}
    </Tag>
  );
}

/** One thin 0..100 bar. The colour carries the meaning; the label is two or three letters of mono. */
export function Bar({ label, value, color, title }: { label: string; value: number; color: string; title?: string }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-1.5" title={title ?? `${label} ${Math.round(pct)}%`}>
      <span className="mono w-6 shrink-0 text-[9px] uppercase tracking-[0.1em] text-fog">{label}</span>
      <span className="h-[3px] flex-1 rounded-full" style={{ background: 'var(--color-slate-700)' }}>
        <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </span>
      <span className="mono w-6 shrink-0 text-right text-[9px] text-fog">{Math.round(pct)}</span>
    </div>
  );
}

/**
 * The family's identity mark: a tile clipped to the family shape (rectangle, diamond, circle, hexagon,
 * chevron). Two stacked clipped layers give it an outline, because a clip-path cuts a border off.
 */
export function ShapeTile({
  meta, size = 46, active = false, solid = false, children,
}: { meta: FamilyMeta; size?: number; active?: boolean; solid?: boolean; children?: ReactNode }) {
  return (
    <span className="relative inline-block" style={{ width: size, height: size }}>
      <span
        className={`absolute inset-0 shape-${meta.shape}`}
        style={{ background: meta.color, opacity: active ? 1 : 0.75 }}
      />
      {!solid && (
        <span
          className={`absolute shape-${meta.shape}`}
          style={{
            inset: 2,
            background: active
              ? `color-mix(in srgb, ${meta.color} 26%, var(--color-slate-800))`
              : 'var(--color-slate-800)',
          }}
        />
      )}
      <span className="absolute inset-0 flex items-center justify-center">{children}</span>
    </span>
  );
}

/**
 * The three costs every order card carries (spec §5): issue seconds of command-net time, the estimated
 * command-and-control transmission delay, and the unit's own execution time.
 */
export function CostMarks({ issueSec, txSec, execSec, color }: { issueSec: number; txSec: number; execSec: number; color: string }) {
  return (
    <div className="mono flex items-center gap-1 text-[9px] text-fog">
      <Mark glyph="I" value={fmtDur(issueSec)} color={color} title={`Issue: ${issueSec} s of command-net time`} />
      <Mark glyph="T" value={fmtDur(txSec)} color="var(--color-warn)" title={`Transmit: about ${fmtDur(txSec)} of command-and-control delay`} />
      <Mark glyph="X" value={fmtDur(execSec)} color="var(--color-fog)" title={`Execute: ${fmtDur(execSec)} for the unit to act`} />
    </div>
  );
}

function Mark({ glyph, value, color, title }: { glyph: string; value: string; color: string; title: string }) {
  return (
    <span className="flex items-center gap-0.5 rounded-sm border border-slate-700 px-1 py-px" title={title}>
      <span style={{ color }}>{glyph}</span>
      <span className="text-paper">{value}</span>
    </span>
  );
}

/** Durations read as seconds under a minute and m:ss above it. */
export function fmtDur(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
